'use client';

import React, { useState } from 'react';

interface Video {
  id: number;
  title: string;
  category: string;
  desc: string;
  duration: string;
  comingSoon?: boolean;
}

const videos: Video[] = [
  {
    id: 1,
    title: 'End-to-End SAC and SAC Planning Demo',
    category: 'Demo',
    desc: 'A comprehensive walkthrough of SAC and SAC Planning — from model setup to planning story design and data action execution.',
    duration: '2 hrs',
  },
  {
    id: 2,
    title: 'SAP Activate Methodology for SAC Planning',
    category: 'Methodology',
    desc: 'How SAP Activate phases — Discover, Prepare, Explore, Realise, Deploy, Run — apply specifically to SAC Planning implementations.',
    duration: '2 hrs',
  },
  {
    id: 3,
    title: 'Enterprise Needs Discovery & Mapping',
    category: 'Discovery',
    desc: 'Structured approach to uncovering enterprise planning requirements — stakeholder mapping, process discovery, and gap analysis.',
    duration: '2 hrs',
  },
  {
    id: 4,
    title: 'Use Case Development: End-to-End Walkthrough',
    category: 'Use Case',
    desc: 'Full lifecycle of a single SAC Planning use case — from business requirement through model design, data action build, and story delivery.',
    duration: '4 hrs',
  },
  {
    id: 5,
    title: 'Planning Model Design Fundamentals',
    category: 'Model Design',
    desc: 'Core principles of SAC Planning model architecture — dimensions, measures, versions, and star schema design.',
    duration: 'Coming Soon',
    comingSoon: true,
  },
  {
    id: 6,
    title: 'Data Actions: Patterns and Best Practices',
    category: 'Data Actions',
    desc: 'Design patterns for copy, allocation, and initialisation data actions with performance and maintainability in focus.',
    duration: 'Coming Soon',
    comingSoon: true,
  },
];

const categories = ['All', ...Array.from(new Set(videos.map((v) => v.category)))];

export default function ExplainerContent() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = videos.filter(
    (v) => activeCategory === 'All' || v.category === activeCategory
  );

  return (
    <div className="py-12 px-6">
      <div className="max-w-container mx-auto">
        {/* Category filters */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`tag-pill cursor-pointer transition-all duration-150 ${
                  activeCategory === cat
                    ? 'bg-primary text-white border-primary' :'hover:border-primary hover:text-foreground'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Count */}
        <div className="mono-label mb-6">
          {filtered.length} video{filtered.length !== 1 ? 's' : ''}
          {activeCategory !== 'All' && ` in ${activeCategory}`}
        </div>

        {/* Video grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((video) => (
            <div
              key={video.id}
              className={`border border-border p-6 bg-white ${
                video.comingSoon ? 'opacity-60' : ''
              }`}
              style={{ borderRadius: '4px' }}
            >
              {/* Top row */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <span className="mono-label">{String(video.id).padStart(2, '0')}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="tag-pill text-xs">{video.category}</span>
                  <span
                    className={`text-xs font-mono px-2 py-0.5 border ${
                      video.comingSoon
                        ? 'border-border text-muted' :'border-foreground text-foreground'
                    }`}
                    style={{ borderRadius: '2px' }}
                  >
                    {video.duration}
                  </span>
                </div>
              </div>

              {/* Title */}
              <h3 className="font-heading font-semibold text-foreground text-base mb-2 leading-snug">
                {video.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-muted font-body leading-relaxed mb-4">{video.desc}</p>

              {/* Play area */}
              {video.comingSoon ? (
                <div
                  className="w-full h-36 bg-secondary border border-border flex items-center justify-center"
                  style={{ borderRadius: '4px' }}
                >
                  <span className="mono-label text-muted">Coming Soon</span>
                </div>
              ) : (
                <div
                  className="w-full h-36 bg-secondary border border-border flex items-center justify-center group cursor-pointer hover:bg-primary/5 transition-colors duration-200"
                  style={{ borderRadius: '4px' }}
                >
                  {/* Play icon */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 border border-foreground flex items-center justify-center group-hover:bg-foreground transition-colors duration-200" style={{ borderRadius: '50%' }}>
                      <svg
                        className="w-4 h-4 text-foreground group-hover:text-white transition-colors duration-200 ml-0.5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <span className="mono-label text-muted text-xs">Video coming soon</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Info note */}
        <div className="mt-10 pt-8 border-t border-border">
          <p className="text-sm text-muted font-body">
            Videos are being added progressively. Each video is structured for planning professionals
            and implementation teams working with SAC Planning.
          </p>
        </div>
      </div>
    </div>
  );
}
